/*
  Honorium scroll-reveal animations.
  Adds .iot-reveal to selected elements and flips them to .iot-reveal--visible
  when they scroll into view. Content is never hidden without JS because the
  classes are only added here.
*/
(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Back-to-top button: functional, so it exists regardless of motion preference.
  const initBackToTop = () => {
    if (document.querySelector('.iot-back-top')) return;
    const button = document.createElement('button');
    button.className = 'iot-back-top';
    button.type = 'button';
    button.setAttribute('aria-label', 'Back to top');
    button.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>';
    button.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion.matches ? 'auto' : 'smooth' });
    });
    document.body.appendChild(button);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        button.classList.toggle('iot-back-top--visible', window.scrollY > 600);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  // Buy-2 hint in the cart drawer: when exactly one item is in the cart,
  // suggest adding a second one for the deal.
  const initCartUpsell = () => {
    const dialog = document.querySelector('.cart-drawer__dialog');
    if (document.documentElement.dataset.iotCartUpsellBound) return;
    document.documentElement.dataset.iotCartUpsellBound = 'true';

    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-iot-cart-upsell-button]');
      if (!button) return;

      const upsellRow = button.closest('.iot-cart-upsell-row');
      const productRow = upsellRow && upsellRow.previousElementSibling;
      const scope = productRow || button.closest('.cart-items-component') || document;
      const plus = scope.querySelector('.quantity-selector button[name="plus"], .quantity-selector button:last-of-type');

      if (plus) {
        button.disabled = true;
        plus.click();
      }
    });

    if (!dialog || dialog.dataset.iotUpsellCleaned) return;
    dialog.dataset.iotUpsellCleaned = 'true';

    let evaluateTimer;
    const evaluate = () => {
      clearTimeout(evaluateTimer);
      evaluateTimer = setTimeout(async () => {
        dialog
          .querySelectorAll('.cart-drawer__summary > .iot-cart-upsell, .cart-drawer__content > .iot-cart-upsell')
          .forEach((legacyUpsell) => legacyUpsell.remove());
      }, 250);
    };

    const observer = new MutationObserver((mutations) => {
      // Ignore mutations caused by the hint itself to avoid churn.
      const relevant = mutations.some((mutation) => {
        const target = mutation.target;
        return !(target instanceof Element && target.closest('.iot-cart-upsell'));
      });
      if (relevant) evaluate();
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    observer.observe(dialog, { childList: true, subtree: true });
  };

  const initJudgeMeStarFix = () => {
    if (document.documentElement.dataset.iotJudgeMeStarFix === 'true') return;
    document.documentElement.dataset.iotJudgeMeStarFix = 'true';

    const privateUseGlyphs = /[\uE000-\uF8FF]/;
    const starText = '\u2605\u2605\u2605\u2605\u2605';

    const repairStars = (root = document) => {
      root.querySelectorAll('.jdgm-widget').forEach((widget) => {
        widget.querySelectorAll('*').forEach((element) => {
          if (!(element instanceof HTMLElement)) return;
          if (element.dataset.iotStarsFixed === 'true') return;

          const text = (element.textContent || '').trim();
          if (!privateUseGlyphs.test(text)) return;

          const compact = text.replace(/\s+/g, '');
          if (!compact || !/^[\uE000-\uF8FF]+$/.test(compact)) return;

          element.dataset.iotStarsFixed = 'true';
          element.setAttribute('aria-label', `${Math.min(compact.length, 5)} out of 5 stars`);
          element.textContent = starText.slice(0, Math.min(compact.length, 5));
          element.style.setProperty('color', '#8fd86a', 'important');
          element.style.setProperty('font-family', 'Arial, Helvetica, sans-serif', 'important');
          element.style.setProperty('font-size', '18px', 'important');
          element.style.setProperty('font-weight', '900', 'important');
          element.style.setProperty('letter-spacing', '2px', 'important');
          element.style.setProperty('line-height', '1', 'important');
          element.style.setProperty('text-shadow', '0 0 12px rgba(143, 216, 106, 0.34)', 'important');
        });
      });
    };

    repairStars();
    window.setTimeout(() => repairStars(), 600);
    window.setTimeout(() => repairStars(), 1800);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) repairStars(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const initReviewsPageFallback = () => {
    const fallback = document.querySelector('.iot-store-reviews-fallback');
    const source = document.querySelector('.iot-judge-reviews-source');

    if (!fallback || !source) return;

    const updateFallbackVisibility = () => {
      const hasRealReviews = source.querySelector('.jdgm-rev, .jdgm-all-reviews__body, .jdgm-rev-widg__reviews');
      fallback.classList.toggle('is-hidden', Boolean(hasRealReviews));
    };

    updateFallbackVisibility();
    window.setTimeout(updateFallbackVisibility, 800);
    window.setTimeout(updateFallbackVisibility, 2500);

    const observer = new MutationObserver(updateFallbackVisibility);
    observer.observe(source, { childList: true, subtree: true });
  };

  const initLanguagePopup = () => {
    const popup = document.querySelector('.honorium-language-popup');
    if (!popup) return;

    const storageKey = 'honorium-language-choice-v1';
    const currentLanguage = popup.dataset.currentLanguage || document.documentElement.lang || '';
    const browserLanguage = (navigator.language || '').slice(0, 2).toLowerCase();
    const matchingOption = browserLanguage
      ? popup.querySelector(`[data-language-code="${CSS.escape(browserLanguage)}"]`)
      : null;

    if (matchingOption) matchingOption.classList.add('is-suggested');

    const panel = popup.querySelector('.honorium-language-popup__panel');
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let lastFocused = null;

    const close = () => {
      popup.hidden = true;
      document.removeEventListener('keydown', onKeydown, true);
      // Restore focus to whatever was focused before the popup opened.
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
      try {
        window.localStorage.setItem(storageKey, currentLanguage || 'dismissed');
      } catch (error) {
        // Ignore storage failures; the popup can still be closed for this page.
      }
    };

    // Close on Escape and keep Tab focus trapped inside the dialog.
    const onKeydown = (event) => {
      if (popup.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        (panel || popup).querySelectorAll(focusableSelector)
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !(panel || popup).contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const open = () => {
      lastFocused = document.activeElement;
      popup.hidden = false;
      document.addEventListener('keydown', onKeydown, true);
      // Move focus into the dialog so keyboard users land inside it.
      const target = (panel || popup).querySelector(focusableSelector);
      if (target instanceof HTMLElement) target.focus();
    };

    const hasChoice = (() => {
      try {
        return Boolean(window.localStorage.getItem(storageKey));
      } catch (error) {
        return false;
      }
    })();

    if (!hasChoice) {
      window.setTimeout(open, 900);
    }

    popup.querySelectorAll('[data-language-popup-close]').forEach((button) => {
      button.addEventListener('click', close);
    });

    popup.querySelectorAll('form').forEach((form) => {
      form.addEventListener('submit', () => {
        const value = form.querySelector('input[name="language_code"]')?.value || currentLanguage;
        try {
          window.localStorage.setItem(storageKey, value);
        } catch (error) {
          // Ignore storage failures before submitting to Shopify.
        }
      });
    });
  };

  // "Only 20 left" scarcity badge, top-left over the product photo.
  // Only the sleep/mask core product gets it (detected via the sleep-only
  // reason block that the product template renders, with a title fallback).
  const initStockBadge = () => {
    const media =
      document.querySelector('.product-information__media') ||
      document.querySelector('.media-gallery');
    if (!media || media.querySelector('.iot-stock-badge')) return;

    const titleElement = document.querySelector(
      '.product-title, [class*="product-title"], main[data-template="product"] h1'
    );
    const title = (titleElement?.textContent || '').toLowerCase();
    const isSleepMask =
      document.querySelector('.iot-product-reason--sleep') != null ||
      title.includes('sleep') ||
      title.includes('mask');
    if (!isSleepMask) return;

    if (getComputedStyle(media).position === 'static') {
      media.style.position = 'relative';
    }

    const badge = document.createElement('div');
    badge.className = 'iot-stock-badge';
    badge.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l9-6 9 6v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M3 9h18"/></svg><span>Only 20 left</span>';
    media.appendChild(badge);
  };

  const initFunctional = () => {
    initBackToTop();
    initCartUpsell();
    initJudgeMeStarFix();
    initReviewsPageFallback();
    initLanguagePopup();
    initStockBadge();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFunctional);
  } else {
    initFunctional();
  }

  if (prefersReducedMotion.matches) return;

  // Fireflies: drifting glow dots in the dark hero sections.
  const FIREFLY_HOSTS = [
    '#MainContent[data-template="index"] .shopify-section:first-child',
    '.iot-ambient-wrap',
    '.iot-assortment-hero',
    '.iot-contact-hero',
  ];

  const spawnFireflies = () => {
    // On small screens the first viewport gets busy fast — desktop only.
    if (window.matchMedia('(max-width: 749px)').matches) return;
    FIREFLY_HOSTS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((host) => {
        if (host.querySelector(':scope > .iot-fireflies')) return;
        const wrap = document.createElement('div');
        wrap.className = 'iot-fireflies';
        wrap.setAttribute('aria-hidden', 'true');
        for (let i = 0; i < 7; i++) {
          const fly = document.createElement('span');
          fly.className = 'iot-firefly';
          fly.style.setProperty('--x', `${5 + Math.random() * 90}%`);
          fly.style.setProperty('--y', `${15 + Math.random() * 75}%`);
          fly.style.setProperty('--dur', `${7 + Math.random() * 8}s`);
          fly.style.setProperty('--delay', `${-Math.random() * 12}s`);
          fly.style.setProperty('--size', `${3 + Math.random() * 4}px`);
          wrap.appendChild(fly);
        }
        host.classList.add('iot-has-fireflies');
        host.appendChild(wrap);
      });
    });
  };

  // The body never gets a template- class, so detect the product page via the
  // data-template attribute that the working CSS uses everywhere.
  const productPage = document.querySelector('main[data-template="product"]') != null;

  // Groups whose direct matches reveal one after another (staggered).
  const STAGGER_GROUPS = [
    { container: '.iot-ambient-intro', children: ':scope > *' },
    { container: '.iot-trust-strip', children: '.iot-trust-strip__item' },
    { container: '.iot-benefit-rail', children: '.iot-benefit-rail__item' },
    { container: '.iot-bestsellers__grid', children: '.iot-bestseller-card' },
    { container: '.iot-assortment-hero__inner', children: ':scope > *' },
    { container: '.iot-assortment-cards', children: '.iot-assortment-card' },
    { container: '.iot-contact-hero__inner', children: ':scope > *' },
    { container: '.iot-contact-cards', children: '.iot-contact-card' },
    { container: '.iot-about-hero__inner', children: ':scope > *' },
    { container: '.iot-about-story__grid', children: '.iot-about-story__panel' },
    { container: '.iot-about-promise__list', children: ':scope > div' },
    { container: '.iot-faq', children: '.iot-faq__item' },
    { container: '.iot-trust-row', children: '.iot-trust-row__item' },
    { container: '.cart-items', children: '.cart-items__table-row' },
    { container: '.resource-list', children: '.resource-list__item' },
    { container: '.product-form-buttons', children: ':scope > *' },
  ];

  // Standalone elements that reveal on their own.
  const SINGLE_SELECTORS = [
    '#MainContent .iot-bestsellers .iot-kosso-section__head',
    '#MainContent .section-resource-list__header',
    '#MainContent .iot-buy-two',
    '#MainContent .iot-faq__eyebrow',
    '#MainContent .iot-faq__title',
    '#MainContent .jdgm-widget',
    '#MainContent .contact-form',
    '#MainContent .iot-about-image__frame',
    '#MainContent .iot-about-promise__content',
    '#MainContent .iot-about-cta',
    'footer .footer-content > *',
  ];

  const STAGGER_STEP = 110;
  const revealTargets = new Set();

  const prepare = (element, index) => {
    if (element.classList.contains('iot-reveal')) return;
    // Product pages reveal the details column via animateProduct(); don't let the
    // generic groups also reveal elements inside it (that would double-animate).
    if (productPage && element.closest('.product-details')) return;
    element.classList.add('iot-reveal');
    element.style.setProperty('--iot-stagger', `${Math.min(index, 6) * STAGGER_STEP}ms`);
    revealTargets.add(element);
  };

  const collect = (root) => {
    STAGGER_GROUPS.forEach(({ container, children }) => {
      root.querySelectorAll(container).forEach((group) => {
        group.querySelectorAll(children).forEach(prepare);
      });
    });

    SINGLE_SELECTORS.forEach((selector) => {
      root.querySelectorAll(selector).forEach((element) => prepare(element, 0));
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('iot-reveal--visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
  );

  const run = () => {
    collect(document);
    revealTargets.forEach((element) => observer.observe(element));
    revealTargets.clear();
  };

  // Hero entrance: stagger the first section's content as soon as the page loads.
  const animateHero = () => {
    const firstSection = document.querySelector('#MainContent .shopify-section');
    if (!firstSection) return;
    const blocks = firstSection.querySelectorAll('.text-block, .button, .link');
    blocks.forEach((block, index) => {
      prepare(block, index);
      // Already in the viewport, so reveal on the next frame for the entrance effect.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => block.classList.add('iot-reveal--visible'));
      });
      revealTargets.delete(block);
    });
  };

  // Product page entrance: zoom the gallery in and stagger the detail blocks
  // (title, price, variant picker, buy buttons, trust blocks) as the page loads.
  const animateProduct = () => {
    if (!productPage) return false;
    const section = document.querySelector('#MainContent .shopify-section');
    if (!section) return false;

    const revealNow = (element, index, anim) => {
      if (!element || element.classList.contains('iot-reveal')) return;
      element.classList.add('iot-reveal');
      if (anim) element.setAttribute('data-iot-anim', anim);
      element.style.setProperty('--iot-stagger', `${Math.min(index, 8) * STAGGER_STEP}ms`);
      // Already in the viewport, so reveal on the next frame for the entrance effect.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => element.classList.add('iot-reveal--visible'));
      });
    };

    const media = section.querySelector('.product-information__media');
    revealNow(media, 0, 'left');

    const items = section.querySelectorAll(
      '.product-details > .group-block > .group-block-content > *:not(.view-product-title)'
    );
    items.forEach((item, index) => {
      // The bundle-deal block has badges that overhang its top edge; a sliding,
      // composited reveal (transform/will-change/filter) mispositions them, so
      // leave this block static and let it render exactly as the CSS intends.
      if (item.querySelector('.buy-two-deal-wrapper, .iot-buy-two')) return;
      revealNow(item, index + 1, 'left');
    });

    return Boolean(media || items.length);
  };

  // Mouse-driven effects only make sense with a real (fine) pointer.
  const hasFinePointer = () => window.matchMedia('(pointer: fine)').matches;

  // Cursor glow: a soft mint orb that trails the pointer and grows over
  // interactive elements. Position + scale are eased every frame for smoothness.
  const initCursorGlow = () => {
    if (!hasFinePointer() || document.querySelector('.iot-cursor')) return;

    const dot = document.createElement('div');
    dot.className = 'iot-cursor';
    dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let x = targetX;
    let y = targetY;
    let scale = 1;
    let targetScale = 1;
    let visible = false;

    const interactiveSelector = 'a, button, [role="button"], input, select, textarea, summary, label, .product-card';

    window.addEventListener(
      'mousemove',
      (event) => {
        targetX = event.clientX;
        targetY = event.clientY;
        if (!visible) {
          visible = true;
          dot.classList.add('iot-cursor--active');
        }
        const interactive =
          event.target instanceof Element && event.target.closest(interactiveSelector);
        targetScale = interactive ? 2.4 : 1;
        dot.classList.toggle('iot-cursor--hot', Boolean(interactive));
      },
      { passive: true }
    );

    // Hide when the pointer leaves the window entirely.
    document.addEventListener('mouseleave', () => {
      visible = false;
      dot.classList.remove('iot-cursor--active');
    });

    const render = () => {
      x += (targetX - x) * 0.18;
      y += (targetY - y) * 0.18;
      scale += (targetScale - scale) * 0.15;
      dot.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${scale})`;
      requestAnimationFrame(render);
    };
    requestAnimationFrame(render);
  };

  // Card spotlight: a glow that follows the cursor inside each card.
  // Driven by CSS variables so the actual paint stays on the GPU.
  const SPOTLIGHT_SELECTORS =
    '.product-card, .iot-bestseller-card, .iot-assortment-card, .iot-contact-card, .iot-trust-strip__item, .iot-benefit-rail__item, .iot-about-story__panel';

  const initCardSpotlight = (root = document) => {
    if (!hasFinePointer()) return;
    root.querySelectorAll(SPOTLIGHT_SELECTORS).forEach((card) => {
      if (card.dataset.iotSpotlight) return;
      card.dataset.iotSpotlight = 'true';
      card.classList.add('iot-spotlight');
      card.addEventListener(
        'mousemove',
        (event) => {
          const rect = card.getBoundingClientRect();
          card.style.setProperty('--iot-mx', `${event.clientX - rect.left}px`);
          card.style.setProperty('--iot-my', `${event.clientY - rect.top}px`);
        },
        { passive: true }
      );
    });
  };

  // Magnetic buttons: the key CTAs pull gently toward the cursor on hover.
  const MAGNETIC_SELECTORS = '.add-to-cart-button, .iot-back-top';

  const initMagnetic = (root = document) => {
    if (!hasFinePointer()) return;
    root.querySelectorAll(MAGNETIC_SELECTORS).forEach((el) => {
      if (el.dataset.iotMagnetic) return;
      el.dataset.iotMagnetic = 'true';
      const strength = 0.28;
      el.addEventListener(
        'mousemove',
        (event) => {
          const rect = el.getBoundingClientRect();
          const mx = event.clientX - (rect.left + rect.width / 2);
          const my = event.clientY - (rect.top + rect.height / 2);
          el.style.transform = `translate(${mx * strength}px, ${my * strength}px)`;
        },
        { passive: true }
      );
      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
      });
    });
  };

  // About-page image band: auto-cycle the active products every few seconds.
  // Each new slide enters from the right, so the motion always reads right-to-left.
  const initAboutSlideshow = (root = document) => {
    root.querySelectorAll('[data-iot-about-slideshow]').forEach((show) => {
      if (show.dataset.iotSlideshowBound) return;
      const slides = Array.from(show.querySelectorAll('.iot-about-slide'));
      if (slides.length < 2) return;
      show.dataset.iotSlideshowBound = 'true';

      let index = 0;
      const advance = () => {
        const current = slides[index];
        const nextIndex = (index + 1) % slides.length;
        const next = slides[nextIndex];

        // Snap the incoming slide to the right edge without animating, so it
        // always slides in from the right (never crosses back the other way).
        next.style.transition = 'none';
        next.classList.remove('is-leaving', 'is-active');
        void next.offsetWidth;
        next.style.transition = '';

        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        next.classList.add('is-active');

        index = nextIndex;
      };

      setInterval(advance, 4200);
    });
  };

  const init = () => {
    if (!animateProduct()) animateHero();
    spawnFireflies();
    run();
    initCursorGlow();
    initCardSpotlight();
    initMagnetic();
    initAboutSlideshow();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-apply when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (event) => {
    if (!animateProduct()) animateHero();
    spawnFireflies();
    run();
    const root = event.target instanceof Element ? event.target : document;
    initCardSpotlight(root);
    initMagnetic(root);
    initAboutSlideshow(root);
    initStockBadge();
  });
})();
